import type { SiteTechStack } from '@/types/auth';

export interface PlatformGuide {
  title: string;
  steps: string[];
  important: string;
  planLink?: { label: string; href: string };
  afterImportant?: string;
}

/**
 * Per-stack install copy. Verified April 2026 against each vendor's docs.
 * Inline formatting: `**bold**` and `` `code` `` are parsed by the renderer.
 * Keep copy in sync with STATUS/PLAN_STEP3_INSTALL_WIZARD.md.
 */
export const PLATFORM_GUIDES: Record<SiteTechStack, PlatformGuide> = {
  gtm: {
    title: 'Google Tag Manager',
    steps: [
      'In your GTM workspace, click **Tags → New → Tag Configuration → Custom HTML**.',
      'Paste the snippet into the HTML field.',
      'Click **Triggering** and select **Initialization - All Pages** (the default trigger that ships with every container).',
      'Click **Save**.',
      'Click **Submit** (top-right), then **Publish and Create Version**.',
    ],
    important:
      "Clicking Save is not enough — you must click **Publish and Create Version** for the tag to go live. If you have EU visitors and a consent management platform, Custom HTML tags don't inherit consent checks automatically — ask your consent provider how to gate third-party tags.",
  },
  gtag: {
    title: 'Google tag (gtag.js)',
    steps: [
      "Open your site's HTML file and find the `<head>` section.",
      'Find the existing Google tag block (starts with `<script async src="https://www.googletagmanager.com/gtag/js"`).',
      'Paste our snippet on a new line immediately after that block, still inside `<head>`.',
      'Save and re-deploy.',
    ],
    important:
      'If you see a "blocked by CSP" error in your browser console, your site has a Content Security Policy — add `api.heymotek.com` to your `script-src` allowlist.',
  },
  wordpress: {
    title: 'WordPress',
    steps: [
      'In wp-admin, go to **Plugins → Add New** and install the free plugin **WPCode – Insert Headers and Footers + Custom Code Snippets** (by WPBeginner).',
      'Activate it. Navigate to **Code Snippets → Header & Footer** in the left sidebar.',
      'Paste the snippet in the **Header** box. Click **Save Changes**.',
    ],
    important:
      "WPCode works on both classic themes and block themes (Twenty Twenty-Four+). Avoid editing `header.php` directly unless you have a child theme — edits will be wiped on the next theme update.",
  },
  shopify: {
    title: 'Shopify',
    steps: [
      'In Shopify admin, go to **Online Store → Themes**.',
      'On your current theme, click the three-dot **•••** menu → **Edit code**.',
      'In the left sidebar under **Layout**, open **theme.liquid**.',
      'Find `</head>` and paste the snippet on a new line immediately before it.',
      'Click **Save** (top right). Changes are live immediately.',
    ],
    important:
      'Duplicate your theme before editing (three-dot menu → Duplicate) — a syntax error in theme.liquid can break your storefront. No developer permissions are needed to edit your own theme.',
  },
  wix: {
    title: 'Wix',
    steps: [
      'In your Wix dashboard, go to **Settings → Custom Code** (under Development & Integrations).',
      'Click **+ Add Custom Code**.',
      'Paste the snippet in the code box.',
      'Set **Add Code to Pages** → **All pages** and placement → **Head**.',
      'Click **Apply**.',
    ],
    important:
      "Wix Custom Code requires a **paid plan** (Light tier or above) AND a connected custom domain (not a free Wix subdomain). If you're on the free plan, you need to upgrade before you can install tracking scripts.",
    planLink: { label: 'Wix pricing →', href: 'https://www.wix.com/upgrade/website' },
    afterImportant:
      'Wix Studio (newer Wix builder): same feature, different UI — look for the Code Panel in the Studio editor.',
  },
  squarespace: {
    title: 'Squarespace',
    steps: [
      'In your Squarespace admin, go to **Settings → Advanced → Code Injection**.',
      'Paste the snippet into the **Header** field (top of the modal).',
      'Click **Save**.',
    ],
    important:
      "Code Injection requires a **Business plan or higher** — free trial and Personal plans can't use custom code. Works on 7.0, 7.1, and Fluid Engine.",
    planLink: { label: 'Squarespace plans →', href: 'https://www.squarespace.com/pricing' },
  },
  webflow: {
    title: 'Webflow',
    steps: [
      'In the Webflow Designer, click the **W** logo (top-left) → **Project Settings**.',
      'Open the **Custom Code** tab.',
      'Paste the snippet into the **Head Code** section.',
      'Click **Save Changes**, then **Publish** your site.',
    ],
    important:
      "Webflow Custom Code requires a paid **Site plan** (or a paid Workspace with a hosted site) — free sites cannot use custom code. Don't paste surrounding `<html>`, `<head>`, or `<body>` tags — Webflow wraps your code automatically. 50,000 character limit per section (plenty).",
  },
  framer: {
    title: 'Framer',
    steps: [
      'Open your Framer project → **Project Settings** → **Custom Code** tab.',
      'Click **+ Add Custom Code**, give it a name (e.g. "HeyMotek").',
      'Paste the snippet.',
      'Set placement to **End of `<head>`** and select **Run on every page visit**.',
      '**Publish** your site.',
    ],
    important:
      "Custom Code is not available on the Free plan — requires **Basic** or higher. If you've configured a Content Security Policy, whitelist `api.heymotek.com`.",
    planLink: { label: 'Framer pricing →', href: 'https://www.framer.com/pricing' },
  },
  astro: {
    title: 'Astro',
    steps: [
      "Open your main layout file (commonly `src/layouts/Layout.astro` or `src/layouts/BaseLayout.astro`).",
      'Find the `<head>` section.',
      'Paste the snippet below inside `<head>`, **including the `is:inline` directive** so Astro emits it as-is instead of trying to bundle it.',
      'Save and re-deploy your site (`npm run build` then push, or your usual deploy command).',
    ],
    important:
      "Astro's compiler tries to bundle every `<script>` it sees in `.astro` files. Adding `is:inline` tells it to leave our snippet alone — without it, you'll get a build hint and the snippet may not load. Outside `.astro` files (e.g. plain HTML), `is:inline` is harmless and silently ignored.",
  },
  bigcommerce: {
    title: 'BigCommerce',
    steps: [
      'In the BigCommerce control panel, go to **Storefront → Script Manager**.',
      'Click **Create a Script**.',
      'Give it a name (e.g. "HeyMotek"), paste the snippet into **Script Contents**.',
      'Set **Location** to **Head** and **Select pages** to **All pages**.',
      'Click **Save**.',
    ],
    important:
      "Script Manager is available on all BigCommerce plans. Scripts don't run on checkout/confirmation pages unless your store has **Optimized One-Page Checkout** enabled (default on newer Stencil stores). Older **Blueprint** themes don't render Script Manager output at all — if you're on Blueprint, upgrade to Stencil first.",
  },
  raw_html: {
    title: 'Generic HTML install',
    steps: [
      "Open your site's main HTML file (usually `index.html`) in a code editor.",
      'Find the closing `</head>` tag near the top of the file.',
      'Paste the snippet on a new line just before `</head>`.',
      'Save and re-deploy your site.',
    ],
    important:
      "**If you use a hosted builder** (GoDaddy, Hostinger, Strikingly, Duda, Carrd, Weebly, etc.) and don't know where to paste: look in your builder's settings for a \"Custom code\" / \"Tracking\" / \"Head\" / \"Embed\" section. If your plan doesn't include custom code, you may need to upgrade.",
  },
  // `unknown` falls back to the same generic HTML guide as raw_html.
  unknown: {
    title: 'Generic HTML install',
    steps: [
      "Open your site's main HTML file (usually `index.html`) in a code editor.",
      'Find the closing `</head>` tag near the top of the file.',
      'Paste the snippet on a new line just before `</head>`.',
      'Save and re-deploy your site.',
    ],
    important:
      "**If you use a hosted builder** (GoDaddy, Hostinger, Strikingly, Duda, Carrd, Weebly, etc.) and don't know where to paste: look in your builder's settings for a \"Custom code\" / \"Tracking\" / \"Head\" / \"Embed\" section. If your plan doesn't include custom code, you may need to upgrade.",
  },
};
